import React, { ReactElement, useEffect, useMemo, useState } from "react";
import {
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Modal,
  Select,
  TextField,
  makeStyles,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import SearchIcon from "@material-ui/icons/Search";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import DragIndicatorIcon from "@material-ui/icons/DragIndicator";
import UpdateAffinityIcon from "../../../images/update-affinity.png";
import ViewStatIcon from "../../../images/view-stat.png";
import { useHistory } from "react-router-dom";
import dayjs from "dayjs";

import theme from "../../../themes";
import { RedirectUri, useEnumStrings } from "../../enumStrings";
import {
  useGetAllChamberMembersQuery,
  useGetAllAffinityProgramPartnersForChamberQuery,
  useDeactivateAffinityProgramPartnerMutation,
  useReactivateAffinityProgramPartnerMutation,
  useUpdateAffinityProgramPartnerMutation,
  AffinityProgramPartnerActivationStatusEnum,
  BannerInput,
  GetAllAffinityProgramPartnersForChamberDocument,
} from "../../../graphql/types";
import useGetChamberFromUserIdInAuth from "../../common/Hooks/useGetChamberFromUserIdInAuth";
import Loader from "../../common/Loader/Loader";
import SuccessDialog from "../../common/SuccessDialog";
import ExportButton from "../../common/ExportButton";
import VideoTutorialButton from "../../common/VideoTutorialButton";

type ActivationStatus = "activated" | "reactivated" | "deactivated";

const mapServerActivationStatus = (
  status: AffinityProgramPartnerActivationStatusEnum
): ActivationStatus => {
  switch (status) {
    case AffinityProgramPartnerActivationStatusEnum.Reactivated:
      return "reactivated";
    case AffinityProgramPartnerActivationStatusEnum.Deactivated:
      return "deactivated";
    case AffinityProgramPartnerActivationStatusEnum.Activated:
    default:
      return "activated";
  }
};

type ActivationRow = {
  id: string;
  programId: string;
  programName: string;
  programLogo?: string | null;
  startDateUtcMilli: number | null;
  endDateUtcMilli: number | null;
  status: ActivationStatus;
  reactivatedOnUtcMilli?: number | null;
  // Fields required to persist a new display order via the update mutation.
  companyName: string;
  title: string;
  description: string;
  redirectLink: string;
  logo: string;
  banner: BannerInput[];
  appPlacementOrder: number | null;
};

const useStyles = makeStyles(() => ({
  pageRoot: {
    overflowX: "hidden",
  },
  headerSection: {
    backgroundColor: theme.palette.primary.main,
    padding: "30px 40px",
    minHeight: "200px",
    [theme.breakpoints.down("sm")]: {
      padding: "16px 16px",
      minHeight: "auto",
    },
  },
  titleRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "10px",
    gap: "16px",
  },
  title: {
    margin: 0,
    fontSize: "32px",
    color: theme.palette.primary.contrastText,
    fontWeight: 700,
    fontFamily: theme.typography.fontFamily,
    [theme.breakpoints.down("sm")]: {
      fontSize: "24px",
    },
  },
  description: {
    fontSize: "18px",
    color: "rgba(255, 255, 255, 0.92)",
    marginTop: "8px",
    lineHeight: 1.55,
    maxWidth: "920px",
    fontFamily: theme.typography.fontFamily,
    [theme.breakpoints.down("sm")]: {
      fontSize: "16px",
    },
  },
  buttonContainer: {
    display: "flex",
    gap: "15px",
    flexWrap: "wrap",
    marginTop: "20px",
  },
  contentContainer: {
    backgroundColor: "#D4A353",
    padding: "0 40px 40px 40px",
    overflowX: "hidden",
    [theme.breakpoints.down("sm")]: {
      padding: 0,
    },
  },
  content: {
    display: "flex",
    background: theme.palette.background.paper,
    minHeight: "70vh",
    borderRadius: "20px",
    padding: "30px",
    flexDirection: "column",
    overflowX: "hidden",
    [theme.breakpoints.down("sm")]: {
      padding: "14px",
      borderRadius: 0,
    },
  },
  searchContainer: {
    display: "flex",
    gap: "20px",
    alignItems: "center",
    marginBottom: "24px",
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      gap: "10px",
    },
  },
  searchBar: {
    backgroundColor: "#fff",
    borderRadius: "8px",
    width: "404px",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
    "& .MuiOutlinedInput-root": {
      height: "45px",
      borderRadius: "8px",
      "& fieldset": { borderColor: "#000000", borderWidth: "1px" },
      "&:hover fieldset": { borderColor: "#000000" },
      "&.Mui-focused fieldset": { borderColor: "#000000 !important" },
    },
    "& .MuiOutlinedInput-input": {
      fontSize: "14px",
      fontWeight: 500,
      color: "#000",
      padding: "0",
      "&::placeholder": { color: "#9E9E9E", opacity: 1 },
    },
  },
  filterSelect: {
    backgroundColor: "#fff",
    borderRadius: "8px",
    minWidth: "260px",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      minWidth: "unset",
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      height: "45px",
      "& fieldset": { borderColor: "#000000" },
      "&:hover fieldset": { borderColor: "#000000" },
      "&.Mui-focused fieldset": { borderColor: "#000000 !important" },
    },
    "& .MuiSelect-select": {
      paddingTop: "12px",
      paddingBottom: "12px",
      paddingRight: "32px",
      color: "#000",
      fontSize: "14px",
      fontWeight: 500,
    },
  },
  clearAllButton: {
    backgroundColor: "#E53935",
    color: theme.palette.primary.contrastText,
    fontWeight: 700,
    borderRadius: "8px",
    padding: "10px 16px",
    fontSize: "12px",
    height: "45px",
    boxShadow: "none",
    textTransform: "uppercase",
    "&:hover": { backgroundColor: "#C71920" },
  },
  tableWrapper: {
    width: "100%",
    overflowX: "hidden",
    border: "1px solid #E0E0E0",
    borderRadius: "12px",
  },
  table: {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
    tableLayout: "fixed",
  },
  th: {
    backgroundColor: "#FFFFFF",
    color: theme.palette.text.primary,
    fontWeight: 700,
    fontSize: "16px",
    padding: "14px 16px",
    textAlign: "left",
    borderBottom: `1px solid ${theme.palette.text.secondary}`,
    verticalAlign: "top",
  },
  thSubtitle: {
    fontSize: "12px",
    color: theme.palette.text.primary,
    fontWeight: 500,
    marginTop: "4px",
  },
  td: {
    padding: "14px 16px",
    fontSize: "14px",
    color: theme.palette.text.primary,
    borderBottom: "1px solid #E8E8E8",
    verticalAlign: "middle",
  },
  rowEven: {
    backgroundColor: "#FFFFFF",
  },
  rowOdd: {
    backgroundColor: "#FAFAFA",
  },
  programCell: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  programLogo: {
    width: "100%",
    maxWidth: "120px",
    height: "auto",
    maxHeight: "80px",
    minWidth: "80px",
    borderRadius: "8px",
    objectFit: "contain",
    backgroundColor: "#F5F5F5",
  },
  dragHandleHeader: {
    width: "44px",
  },
  dragHandleCell: {
    width: "44px",
    textAlign: "center",
    color: theme.palette.text.secondary,
    cursor: "grab",
    userSelect: "none",
    "&:active": { cursor: "grabbing" },
  },
  dragHandleDisabled: {
    color: "#D0D0D0",
    cursor: "default",
  },
  draggingRow: {
    opacity: 0.5,
  },
  dragOverRow: {
    boxShadow: `inset 0 2px 0 0 ${theme.palette.primary.main}`,
  },
  statusReactivated: {
    color: "#E1A731",
    fontWeight: 700,
    fontSize: "14px",
    letterSpacing: "0.04em",
  },
  statusActivated: {
    color: "#2E9646",
    fontWeight: 700,
    fontSize: "14px",
    letterSpacing: "0.04em",
  },
  statusDeactivated: {
    color: theme.palette.text.primary,
    fontWeight: 700,
    fontSize: "14px",
    letterSpacing: "0.04em",
  },
  statusSubtext: {
    color: "#2E9646",
    fontWeight: 600,
    fontSize: "12px",
    marginTop: "4px",
  },
  daysLeft: {
    color: "#2E9646",
    fontWeight: 600,
    fontSize: "12px",
    marginTop: "4px",
  },
  daysLeftUrgent: {
    color: "#E1A731",
    fontWeight: 700,
    fontSize: "12px",
    marginTop: "4px",
  },
  reactivateButton: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    fontWeight: 700,
    borderRadius: "22px",
    padding: "8px 14px",
    fontSize: "12px",
    textTransform: "uppercase",
    minWidth: "120px",
    height: "32px",
    boxShadow: "none",
    "&:hover": { backgroundColor: "#D4A353" },
  },
  deactivateButton: {
    backgroundColor: theme.palette.grey[800],
    color: theme.palette.primary.contrastText,
    fontWeight: 700,
    borderRadius: "22px",
    padding: "8px 14px",
    fontSize: "12px",
    textTransform: "uppercase",
    minWidth: "120px",
    height: "32px",
    boxShadow: "none",
    "&:hover": { backgroundColor: "#000" },
  },
  rightActionGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    alignItems: "stretch",
  },
  smallActionButton: {
    backgroundColor: "#FFFFFF",
    border: `1px solid ${theme.palette.grey[800]}`,
    color: theme.palette.text.primary,
    fontWeight: 600,
    borderRadius: "8px",
    fontSize: "12px",
    padding: "6px 14px",
    textTransform: "none",
    minHeight: "32px",
    boxShadow: "none",
    "&:hover": { backgroundColor: "#F5F5F5" },
  },
  updateProgramBtn: {
    backgroundColor: theme.palette.primary.main,
    border: "none",
    color: "#FFFFFF",
    fontWeight: 700,
    borderRadius: "10px",
    fontSize: "12px",
    padding: "6px 12px",
    textTransform: "none",
    minHeight: "32px",
    whiteSpace: "nowrap",
    boxShadow: "none",
    "&:hover": { backgroundColor: "#C8902B", boxShadow: "none" },
  },
  viewStatsBtn: {
    backgroundColor: "#24272C",
    border: "none",
    color: "#FFFFFF",
    fontWeight: 700,
    borderRadius: "10px",
    fontSize: "12px",
    padding: "6px 12px",
    textTransform: "none",
    minHeight: "32px",
    whiteSpace: "nowrap",
    boxShadow: "none",
    "&:hover": { backgroundColor: "#000000", boxShadow: "none" },
  },
  smallActionIcon: {
    width: "16px",
    height: "16px",
    marginRight: "6px",
  },
  smallActionIconImg: {
    width: "14px",
    height: "14px",
    marginRight: "6px",
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "40px 16px",
    textAlign: "center",
    gap: "16px",
  },
  emptyTitle: {
    fontWeight: 700,
    fontSize: "20px",
    color: theme.palette.text.primary,
    margin: 0,
  },
  emptyBody: {
    color: theme.palette.text.secondary,
    fontSize: "16px",
    margin: 0,
    maxWidth: "520px",
  },
  primaryCta: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    fontWeight: 700,
    borderRadius: "8px",
    padding: "10px 24px",
    textTransform: "uppercase",
    fontSize: "14px",
    boxShadow: "none",
    "&:hover": { backgroundColor: "#D4A353" },
  },
  modal: {
    border: "unset",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  modalCard: {
    width: "560px",
    maxWidth: "92vw",
    backgroundColor: "#E6E6E6",
    borderRadius: "20px",
    padding: "30px",
    position: "relative",
    outline: "none",
    boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.15)",
    boxSizing: "border-box",
  },
  modalClose: {
    position: "absolute",
    top: "12px",
    right: "12px",
    backgroundColor: theme.palette.error.main,
    color: "white",
    width: "32px",
    height: "32px",
    "&:hover": { backgroundColor: "#C71920" },
  },
  modalTitle: {
    fontWeight: 700,
    fontSize: "22px",
    margin: "0 0 16px 0",
    textAlign: "center",
    color: theme.palette.text.primary,
  },
  modalBody: {
    backgroundColor: "#FFFFFF",
    borderRadius: "12px",
    padding: "20px",
    fontSize: "16px",
    lineHeight: 1.6,
    color: theme.palette.text.primary,
  },
  modalActions: {
    display: "flex",
    justifyContent: "center",
    gap: "16px",
    marginTop: "20px",
    flexWrap: "wrap",
  },
  modalConfirmButton: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.primary.contrastText,
    fontWeight: 700,
    borderRadius: "10px",
    padding: "12px 24px",
    textTransform: "uppercase",
    fontSize: "14px",
    minWidth: "180px",
    height: "44px",
    boxShadow: "none",
    "&:hover": { backgroundColor: theme.palette.error.dark },
  },
  modalCancelButton: {
    backgroundColor: theme.palette.grey[800],
    color: theme.palette.primary.contrastText,
    fontWeight: 700,
    borderRadius: "10px",
    padding: "12px 24px",
    textTransform: "uppercase",
    fontSize: "14px",
    minWidth: "180px",
    height: "44px",
    boxShadow: "none",
    "&:hover": { backgroundColor: "#000" },
  },
  mobileCardWrap: {
    display: "none",
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      flexDirection: "column",
      gap: "12px",
    },
  },
  desktopTableWrap: {
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  mobileCard: {
    backgroundColor: "#FFFFFF",
    border: "1px solid #E0E0E0",
    borderRadius: "12px",
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  mobileRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontSize: "14px",
    color: theme.palette.text.primary,
  },
  mobileLabel: {
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
  mobileActions: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    marginTop: "6px",
  },
}));

const formatDate = (utcMilli: number | null): string => {
  if (!utcMilli) return "-";
  return dayjs(utcMilli).format("MMM D, YYYY");
};

const computeDaysLeft = (endDateUtcMilli: number | null): number | null => {
  if (!endDateUtcMilli) return null;
  const days = Math.ceil((endDateUtcMilli - Date.now()) / (1000 * 60 * 60 * 24));
  return days > 0 ? days : 0;
};

const ManageAffinityProgramActivations = (): ReactElement => {
  const classes = useStyles();
  const enumStrings = useEnumStrings();
  const history = useHistory();

  const chamberInfo = useGetChamberFromUserIdInAuth();
  const chamberId = chamberInfo?.id;

  const { data: allChamberMemberData, loading: chamberMembersLoading } =
    useGetAllChamberMembersQuery({
      variables: { chamberId: chamberId || "" },
      skip: !chamberId,
      fetchPolicy: "cache-and-network",
    });

  const {
    data: partnersData,
    loading: partnersLoading,
    refetch: refetchPartners,
  } = useGetAllAffinityProgramPartnersForChamberQuery({
    skip: !chamberId,
    fetchPolicy: "cache-and-network",
  });

  const isLoading =
    (chamberMembersLoading && !allChamberMemberData) ||
    (partnersLoading && !partnersData);

  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState<ActivationStatus | "all">(
    "all"
  );
  // Locally-held ordering of programs. Drives both the display order and the
  // appPlacementOrder persisted to the database when a user drags a row.
  const [orderedRows, setOrderedRows] = useState<ActivationRow[]>([]);
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const [isSavingOrder, setIsSavingOrder] = useState(false);
  const [deactivateTarget, setDeactivateTarget] = useState<ActivationRow | null>(
    null
  );
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [reactivateTarget, setReactivateTarget] = useState<ActivationRow | null>(
    null
  );
  const [isReactivating, setIsReactivating] = useState(false);
  const [dialogState, setDialogState] = useState<{
    open: boolean;
    message: string;
    variant: "success" | "error";
  }>({ open: false, message: "", variant: "success" });

  const [deactivateAffinityProgramPartner] =
    useDeactivateAffinityProgramPartnerMutation();
  const [reactivateAffinityProgramPartner] =
    useReactivateAffinityProgramPartnerMutation();
  const [updateAffinityProgramPartner] =
    useUpdateAffinityProgramPartnerMutation();

  const allRows: ActivationRow[] = useMemo(() => {
    const partners =
      partnersData?.getAllAffinityProgramPartnersForChamber ?? [];
    return partners.map((partner) => {
      const status = mapServerActivationStatus(partner.activationStatus);
      const banner: BannerInput[] = (partner.banner ?? []).map((b) => ({
        contentUrl: b.contentUrl,
        contentType: b.contentType,
        imageLink: b.imageLink ?? null,
        position: b.position,
      }));
      return {
        id: partner.id,
        programId: partner.id,
        programName:
          partner.companyName || partner.title || "Affinity Program",
        programLogo: partner.logo,
        startDateUtcMilli: partner.activationStartDateUtcMilli ?? null,
        endDateUtcMilli: partner.activationEndDateUtcMilli ?? null,
        status,
        reactivatedOnUtcMilli:
          status === "reactivated"
            ? partner.reactivationInfo?.activationStartDateUtcMilli ?? null
            : null,
        companyName: partner.companyName,
        title: partner.title,
        description: partner.description,
        redirectLink: partner.redirectLink,
        logo: partner.logo,
        banner,
        appPlacementOrder: partner.appPlacementOrder ?? null,
      };
    });
  }, [partnersData]);

  // Keep the local ordering in sync with what the server returns, ordering by
  // the persisted appPlacementOrder first and falling back to name.
  useEffect(() => {
    const sorted = [...allRows].sort((a, b) => {
      const aOrder = a.appPlacementOrder;
      const bOrder = b.appPlacementOrder;
      if (aOrder != null && bOrder != null && aOrder !== bOrder) {
        return aOrder - bOrder;
      }
      if (aOrder != null && bOrder == null) return -1;
      if (aOrder == null && bOrder != null) return 1;
      return a.programName.localeCompare(b.programName);
    });
    setOrderedRows(sorted);
  }, [allRows]);

  const visibleRows = useMemo(() => {
    const lowered = searchText.trim().toLowerCase();
    return orderedRows.filter((row) => {
      if (statusFilter !== "all" && row.status !== statusFilter) return false;
      if (!lowered) return true;
      return row.programName.toLowerCase().includes(lowered);
    });
  }, [orderedRows, statusFilter, searchText]);

  // Reordering is only meaningful against the full, unfiltered list so that
  // dragged positions map directly to the persisted order.
  const reorderEnabled = !searchText.trim() && statusFilter === "all";

  const handleClearAllFilters = () => {
    setSearchText("");
    setStatusFilter("all");
  };

  const persistOrder = async (ordered: ActivationRow[]) => {
    const updates = ordered
      .map((row, idx) => ({ row, newOrder: idx + 1 }))
      .filter(({ row, newOrder }) => row.appPlacementOrder !== newOrder);
    if (updates.length === 0) return;

    setIsSavingOrder(true);
    try {
      await Promise.all(
        updates.map(({ row, newOrder }) =>
          updateAffinityProgramPartner({
            variables: {
              id: row.programId,
              input: {
                id: row.programId,
                companyName: row.companyName,
                title: row.title,
                description: row.description,
                redirectLink: row.redirectLink,
                logo: row.logo,
                banner: row.banner.length > 0 ? row.banner : undefined,
                appPlacementOrder: newOrder,
              },
            },
          })
        )
      );
      // Reflect the newly-saved order numbers locally so a subsequent
      // cache-and-network refetch doesn't appear to revert the order.
      setOrderedRows((prev) =>
        prev.map((row) => {
          const match = ordered.findIndex((r) => r.id === row.id);
          return match === -1
            ? row
            : { ...row, appPlacementOrder: match + 1 };
        })
      );
      void refetchPartners();
    } catch (error) {
      setDialogState({
        open: true,
        variant: "error",
        message: enumStrings.manageAffinityProgramActivations.orderUpdateFailed,
      });
    } finally {
      setIsSavingOrder(false);
    }
  };

  const handleRowDrop = (targetId: string) => {
    setDragOverId(null);
    const sourceId = draggedId;
    setDraggedId(null);
    if (!sourceId || sourceId === targetId || !reorderEnabled) return;

    const next = [...orderedRows];
    const fromIdx = next.findIndex((r) => r.id === sourceId);
    const toIdx = next.findIndex((r) => r.id === targetId);
    if (fromIdx === -1 || toIdx === -1) return;

    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    setOrderedRows(next);
    void persistOrder(next);
  };

  const handleDeactivate = async () => {
    if (!deactivateTarget) return;
    setIsDeactivating(true);
    try {
      await deactivateAffinityProgramPartner({
        variables: { id: deactivateTarget.programId },
        refetchQueries: [
          { query: GetAllAffinityProgramPartnersForChamberDocument },
        ],
        awaitRefetchQueries: true,
      });
      setDialogState({
        open: true,
        variant: "success",
        message: enumStrings.manageAffinityProgramActivations.deactivateSuccess.replace(
          "{{programName}}",
          deactivateTarget.programName
        ),
      });
      setDeactivateTarget(null);
    } catch (error) {
      setDialogState({
        open: true,
        variant: "error",
        message: enumStrings.manageAffinityProgramActivations.deactivateFailed,
      });
    } finally {
      setIsDeactivating(false);
    }
  };

  const handleReactivate = (row: ActivationRow) => {
    setReactivateTarget(row);
  };

  const handleConfirmReactivate = async () => {
    if (!reactivateTarget) return;
    setIsReactivating(true);
    try {
      await reactivateAffinityProgramPartner({
        variables: { id: reactivateTarget.programId },
        refetchQueries: [
          { query: GetAllAffinityProgramPartnersForChamberDocument },
        ],
        awaitRefetchQueries: true,
      });
      setDialogState({
        open: true,
        variant: "success",
        message: enumStrings.manageAffinityProgramActivations.reactivateSuccess.replace(
          "{{programName}}",
          reactivateTarget.programName
        ),
      });
      setReactivateTarget(null);
    } catch (error) {
      setDialogState({
        open: true,
        variant: "error",
        message: enumStrings.manageAffinityProgramActivations.reactivateFailed,
      });
    } finally {
      setIsReactivating(false);
    }
  };

  const handleViewStats = (row: ActivationRow) => {
    history.push(RedirectUri.perkPartnersViewStats, {
      programId: row.programId,
      programName: row.programName,
    });
  };

  const handleUpdateProgram = (row: ActivationRow) => {
    history.push(RedirectUri.perkPartnersUpdateAffinity, {
      programId: row.programId,
      programName: row.programName,
    });
  };

  const renderStatus = (row: ActivationRow) => {
    if (row.status === "reactivated") {
      return (
        <div>
          <span className={classes.statusReactivated}>
            {enumStrings.manageAffinityProgramActivations.statusReactivated}
          </span>
          {row.reactivatedOnUtcMilli && (
            <div className={classes.statusSubtext}>
              {enumStrings.manageAffinityProgramActivations.statusReactivatedAsOf.replace(
                "{{date}}",
                formatDate(row.reactivatedOnUtcMilli)
              )}
            </div>
          )}
        </div>
      );
    }
    if (row.status === "activated") {
      return (
        <span className={classes.statusActivated}>
          {enumStrings.manageAffinityProgramActivations.statusActivated}
        </span>
      );
    }
    return (
      <span className={classes.statusDeactivated}>
        {enumStrings.manageAffinityProgramActivations.statusDeactivated}
      </span>
    );
  };

  const renderActionButtons = (row: ActivationRow) => {
    if (row.status === "deactivated") return null;
    return (
      <div
        style={{
          display: "flex",
          gap: "8px",
          flexDirection: "column",
          alignItems: "flex-start",
        }}
      >
        <Button
          variant="contained"
          className={classes.reactivateButton}
          onClick={() => handleReactivate(row)}
        >
          {enumStrings.manageAffinityProgramActivations.reactivate}
        </Button>
        <Button
          variant="contained"
          className={classes.deactivateButton}
          onClick={() => setDeactivateTarget(row)}
        >
          {enumStrings.manageAffinityProgramActivations.deactivate}
        </Button>
      </div>
    );
  };

  const renderRightActions = (row: ActivationRow) => {
    if (row.status === "deactivated") {
      return (
        <div className={classes.rightActionGroup}>
          <Button
            className={classes.updateProgramBtn}
            onClick={() => handleUpdateProgram(row)}
          >
            <img
              src={UpdateAffinityIcon}
              alt=""
              className={classes.smallActionIconImg}
              style={{ filter: "brightness(0) invert(1)" }}
            />
            {enumStrings.manageAffinityProgramActivations.viewDeactivated}
          </Button>
          <Button
            className={classes.viewStatsBtn}
            onClick={() => handleViewStats(row)}
          >
            <img
              src={ViewStatIcon}
              alt=""
              className={classes.smallActionIconImg}
              style={{ filter: "brightness(0) invert(1)" }}
            />
            {enumStrings.manageAffinityProgramActivations.viewStats}
          </Button>
        </div>
      );
    }
    return (
      <div className={classes.rightActionGroup}>
        <Button
          className={classes.updateProgramBtn}
          onClick={() => handleUpdateProgram(row)}
        >
          <img
            src={UpdateAffinityIcon}
            alt=""
            className={classes.smallActionIconImg}
            style={{ filter: "brightness(0) invert(1)" }}
          />
          {enumStrings.manageAffinityProgramActivations.updateProgram}
        </Button>
        <Button
          className={classes.viewStatsBtn}
          onClick={() => handleViewStats(row)}
        >
          <img
            src={ViewStatIcon}
            alt=""
            className={classes.smallActionIconImg}
            style={{ filter: "brightness(0) invert(1)" }}
          />
          {enumStrings.manageAffinityProgramActivations.viewStats}
        </Button>
      </div>
    );
  };

  return (
    <div className={classes.pageRoot}>
      <div className={classes.headerSection}>
        <div className={classes.titleRow}>
          <div>
            <h1 className={classes.title}>
              {enumStrings.manageAffinityProgramActivations.pageTitle}
            </h1>
            <p className={classes.description}>
              {enumStrings.manageAffinityProgramActivations.pageDescription}
            </p>
          </div>
        </div>
        <div className={classes.buttonContainer}>
          <VideoTutorialButton
            label={enumStrings.manageAffinityProgramActivations.watchTutorials}
            tutorialUrl=""
          />
          <ExportButton
            customOnClick={() => undefined}
            disablePadding
            icon={
              <InfoOutlinedIcon
                style={{
                  color: theme.palette.primary.main,
                  marginRight: "10px",
                  width: "24px",
                  height: "24px",
                }}
              />
            }
            label={enumStrings.manageAffinityProgramActivations.viewInstructions}
          />
        </div>
      </div>

      <div className={classes.contentContainer}>
        <div className={classes.content}>
          <div className={classes.searchContainer}>
            <TextField
              className={classes.searchBar}
              variant="outlined"
              placeholder={
                enumStrings.manageAffinityProgramActivations.searchPlaceholder
              }
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: "#9E9E9E" }} />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl variant="outlined" className={classes.filterSelect}>
              <Select
                value={statusFilter}
                onChange={(e) =>
                  setStatusFilter(e.target.value as ActivationStatus | "all")
                }
                inputProps={{ "aria-label": "Filter by activation status" }}
                displayEmpty
              >
                <MenuItem value="all">
                  {enumStrings.manageAffinityProgramActivations.filterByStatus}
                </MenuItem>
                <MenuItem value="activated">
                  {enumStrings.manageAffinityProgramActivations.statusActivated}
                </MenuItem>
                <MenuItem value="reactivated">
                  {
                    enumStrings.manageAffinityProgramActivations
                      .statusReactivated
                  }
                </MenuItem>
                <MenuItem value="deactivated">
                  {
                    enumStrings.manageAffinityProgramActivations
                      .statusDeactivated
                  }
                </MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="contained"
              className={classes.clearAllButton}
              onClick={handleClearAllFilters}
            >
              {enumStrings.manageAffinityProgramActivations.clearAllFilters}
            </Button>
          </div>

          {isLoading ? (
            <Loader />
          ) : visibleRows.length === 0 ? (
            <div className={classes.emptyState}>
              <h3 className={classes.emptyTitle}>
                {enumStrings.manageAffinityProgramActivations.emptyStateTitle}
              </h3>
              <p className={classes.emptyBody}>
                {enumStrings.manageAffinityProgramActivations.emptyStateBody}
              </p>
              <Button
                variant="contained"
                className={classes.primaryCta}
                onClick={() =>
                  history.push(RedirectUri.perkPartnersActivateAffinity)
                }
              >
                {enumStrings.manageAffinityProgramActivations.emptyStateCta}
              </Button>
            </div>
          ) : (
            <>
              <div className={classes.desktopTableWrap}>
                <div className={classes.tableWrapper}>
                  <table className={classes.table}>
                    <thead>
                      <tr>
                        <th
                          className={`${classes.th} ${classes.dragHandleHeader}`}
                        />
                        <th className={classes.th}>
                          {
                            enumStrings.manageAffinityProgramActivations
                              .columnCompanyName
                          }
                          <div className={classes.thSubtitle}>
                            {
                              enumStrings.manageAffinityProgramActivations
                                .columnCompanyNameSubtitle
                            }
                          </div>
                        </th>
                        <th className={classes.th}>
                          {
                            enumStrings.manageAffinityProgramActivations
                              .columnActivationStatus
                          }
                        </th>
                        <th className={classes.th}>
                          {
                            enumStrings.manageAffinityProgramActivations
                              .columnAction
                          }
                          <div className={classes.thSubtitle}>
                            {
                              enumStrings.manageAffinityProgramActivations
                                .columnActionSubtitle
                            }
                          </div>
                        </th>
                        <th className={classes.th}>
                          {
                            enumStrings.manageAffinityProgramActivations
                              .columnStartDate
                          }
                          <div className={classes.thSubtitle}>
                            {
                              enumStrings.manageAffinityProgramActivations
                                .columnStartDateSubtitle
                            }
                          </div>
                        </th>
                        <th className={classes.th}>
                          {
                            enumStrings.manageAffinityProgramActivations
                              .columnEndDate
                          }
                          <div className={classes.thSubtitle}>
                            {
                              enumStrings.manageAffinityProgramActivations
                                .columnEndDateSubtitle
                            }
                          </div>
                        </th>
                        <th
                          className={classes.th}
                          style={{ textAlign: "right" }}
                        />
                      </tr>
                    </thead>
                    <tbody>
                      {visibleRows.map((row, idx) => {
                        const daysLeft = computeDaysLeft(row.endDateUtcMilli);
                        const rowClassNames = [
                          idx % 2 === 0 ? classes.rowEven : classes.rowOdd,
                          draggedId === row.id ? classes.draggingRow : "",
                          dragOverId === row.id ? classes.dragOverRow : "",
                        ]
                          .filter(Boolean)
                          .join(" ");
                        return (
                          <tr
                            key={row.id}
                            className={rowClassNames}
                            draggable={reorderEnabled && !isSavingOrder}
                            onDragStart={() =>
                              reorderEnabled && setDraggedId(row.id)
                            }
                            onDragOver={(e) => {
                              if (!reorderEnabled || !draggedId) return;
                              e.preventDefault();
                              if (dragOverId !== row.id) setDragOverId(row.id);
                            }}
                            onDragLeave={() => {
                              if (dragOverId === row.id) setDragOverId(null);
                            }}
                            onDrop={(e) => {
                              e.preventDefault();
                              handleRowDrop(row.id);
                            }}
                            onDragEnd={() => {
                              setDraggedId(null);
                              setDragOverId(null);
                            }}
                          >
                            <td
                              className={`${classes.td} ${
                                classes.dragHandleCell
                              } ${
                                reorderEnabled ? "" : classes.dragHandleDisabled
                              }`}
                              title={
                                reorderEnabled
                                  ? "Drag to reorder"
                                  : "Clear search and filters to reorder"
                              }
                            >
                              <DragIndicatorIcon fontSize="small" />
                            </td>
                            <td className={classes.td}>
                              <div className={classes.programCell}>
                                {row.programLogo && (
                                  <img
                                    src={row.programLogo}
                                    alt=""
                                    className={classes.programLogo}
                                  />
                                )}
                                <span style={{ fontWeight: 600 }}>
                                  {row.programName}
                                </span>
                              </div>
                            </td>
                            <td className={classes.td}>{renderStatus(row)}</td>
                            <td className={classes.td}>
                              {renderActionButtons(row)}
                            </td>
                            <td className={classes.td}>
                              {row.startDateUtcMilli
                                ? formatDate(row.startDateUtcMilli)
                                : "-"}
                            </td>
                            <td className={classes.td}>
                              {row.endDateUtcMilli ? (
                                <div>
                                  {formatDate(row.endDateUtcMilli)}
                                  {daysLeft !== null && (
                                    <div
                                      className={
                                        daysLeft <= 30
                                          ? classes.daysLeftUrgent
                                          : classes.daysLeft
                                      }
                                    >
                                      {enumStrings.manageAffinityProgramActivations.daysLeft.replace(
                                        "{{count}}",
                                        String(daysLeft)
                                      )}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                "-"
                              )}
                            </td>
                            <td className={classes.td}>
                              {renderRightActions(row)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Mobile card view */}
              <div className={classes.mobileCardWrap}>
                {visibleRows.map((row) => {
                  const daysLeft = computeDaysLeft(row.endDateUtcMilli);
                  return (
                    <div key={row.id} className={classes.mobileCard}>
                      <div className={classes.programCell}>
                        {row.programLogo && (
                          <img
                            src={row.programLogo}
                            alt=""
                            className={classes.programLogo}
                          />
                        )}
                        <span style={{ fontWeight: 700 }}>
                          {row.programName}
                        </span>
                      </div>
                      <div className={classes.mobileRow}>
                        <span className={classes.mobileLabel}>
                          {
                            enumStrings.manageAffinityProgramActivations
                              .columnActivationStatus
                          }
                        </span>
                        {renderStatus(row)}
                      </div>
                      <div className={classes.mobileRow}>
                        <span className={classes.mobileLabel}>
                          {
                            enumStrings.manageAffinityProgramActivations
                              .columnStartDate
                          }
                        </span>
                        <span>
                          {row.startDateUtcMilli
                            ? formatDate(row.startDateUtcMilli)
                            : "-"}
                        </span>
                      </div>
                      <div className={classes.mobileRow}>
                        <span className={classes.mobileLabel}>
                          {
                            enumStrings.manageAffinityProgramActivations
                              .columnEndDate
                          }
                        </span>
                        <span>
                          {row.endDateUtcMilli
                            ? formatDate(row.endDateUtcMilli)
                            : "-"}
                          {daysLeft !== null && (
                            <span className={classes.daysLeft}>
                              {" "}
                              {enumStrings.manageAffinityProgramActivations.daysLeft.replace(
                                "{{count}}",
                                String(daysLeft)
                              )}
                            </span>
                          )}
                        </span>
                      </div>
                      <div className={classes.mobileActions}>
                        {row.status !== "deactivated" && (
                          <>
                            <Button
                              variant="contained"
                              className={classes.reactivateButton}
                              style={{ width: "100%" }}
                              onClick={() => handleReactivate(row)}
                            >
                              {
                                enumStrings.manageAffinityProgramActivations
                                  .reactivate
                              }
                            </Button>
                            <Button
                              variant="contained"
                              className={classes.deactivateButton}
                              style={{ width: "100%" }}
                              onClick={() => setDeactivateTarget(row)}
                            >
                              {
                                enumStrings.manageAffinityProgramActivations
                                  .deactivate
                              }
                            </Button>
                          </>
                        )}
                        <Button
                          className={classes.updateProgramBtn}
                          style={{ width: "100%" }}
                          onClick={() => handleUpdateProgram(row)}
                        >
                          <img
                            src={UpdateAffinityIcon}
                            alt=""
                            className={classes.smallActionIconImg}
                            style={{ filter: "brightness(0) invert(1)" }}
                          />
                          {row.status === "deactivated"
                            ? enumStrings.manageAffinityProgramActivations
                                .viewDeactivated
                            : enumStrings.manageAffinityProgramActivations
                                .updateProgram}
                        </Button>
                        <Button
                          className={classes.viewStatsBtn}
                          style={{ width: "100%" }}
                          onClick={() => handleViewStats(row)}
                        >
                          <img
                            src={ViewStatIcon}
                            alt=""
                            className={classes.smallActionIconImg}
                            style={{ filter: "brightness(0) invert(1)" }}
                          />
                          {enumStrings.manageAffinityProgramActivations.viewStats}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Deactivate confirmation modal */}
      <Modal
        open={Boolean(deactivateTarget)}
        onClose={() => !isDeactivating && setDeactivateTarget(null)}
        className={classes.modal}
      >
        <div className={classes.modalCard}>
          <IconButton
            className={classes.modalClose}
            onClick={() => !isDeactivating && setDeactivateTarget(null)}
            disabled={isDeactivating}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
          <h2 className={classes.modalTitle}>
            {enumStrings.manageAffinityProgramActivations.deactivateModalTitle}
          </h2>
          <div className={classes.modalBody}>
            <p style={{ margin: 0 }}>
              {enumStrings.manageAffinityProgramActivations.deactivateModalBody.replace(
                "{{programName}}",
                deactivateTarget?.programName || ""
              )}
            </p>
          </div>
          <div className={classes.modalActions}>
            <Button
              variant="contained"
              className={classes.modalCancelButton}
              onClick={() => setDeactivateTarget(null)}
              disabled={isDeactivating}
            >
              {enumStrings.manageAffinityProgramActivations.modalCancel}
            </Button>
            <Button
              variant="contained"
              className={classes.modalConfirmButton}
              onClick={handleDeactivate}
              disabled={isDeactivating}
            >
              {isDeactivating ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                enumStrings.manageAffinityProgramActivations.modalConfirm
              )}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Reactivate confirmation modal */}
      <Modal
        open={Boolean(reactivateTarget)}
        onClose={() => !isReactivating && setReactivateTarget(null)}
        className={classes.modal}
      >
        <div className={classes.modalCard}>
          <IconButton
            className={classes.modalClose}
            onClick={() => !isReactivating && setReactivateTarget(null)}
            disabled={isReactivating}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
          <h2 className={classes.modalTitle}>
            {enumStrings.manageAffinityProgramActivations.reactivateModalTitle}
          </h2>
          <div className={classes.modalBody}>
            <p style={{ margin: 0 }}>
              {enumStrings.manageAffinityProgramActivations.reactivateModalBody.replace(
                "{{programName}}",
                reactivateTarget?.programName || ""
              )}
            </p>
          </div>
          <div className={classes.modalActions}>
            <Button
              variant="contained"
              className={classes.modalCancelButton}
              onClick={() => setReactivateTarget(null)}
              disabled={isReactivating}
            >
              {enumStrings.manageAffinityProgramActivations.modalCancel}
            </Button>
            <Button
              variant="contained"
              className={classes.modalConfirmButton}
              onClick={handleConfirmReactivate}
              disabled={isReactivating}
            >
              {isReactivating ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                enumStrings.manageAffinityProgramActivations
                  .reactivateModalConfirm
              )}
            </Button>
          </div>
        </div>
      </Modal>

      <SuccessDialog
        open={dialogState.open}
        onClose={() => setDialogState((prev) => ({ ...prev, open: false }))}
        message={dialogState.message}
        variant={dialogState.variant}
        autoDismissMs={5000}
      />
    </div>
  );
};

export default ManageAffinityProgramActivations;
